import React, { useState } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import { MechanicalButton } from '../common/MechanicalButton';
import { Printer, RotateCcw, Download, Upload, Gauge, Sparkles } from 'lucide-react';
import { WORKSHOP_PRESETS } from '../../types/vehicle';

export const WorkshopHeader: React.FC = () => {
  const { state, dispatch, resetToDefault, exportState, importState } = useRestoMod();
  const [showPresets, setShowPresets] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    const data = exportState();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `restomod_project_${state.project.year}_${state.project.model.toLowerCase().replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content) {
        const success = importState(content);
        if (success) {
          alert('Project imported successfully!');
        } else {
          alert('Failed to parse project JSON.');
        }
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const applyPreset = (presetKey: string) => {
    const preset = WORKSHOP_PRESETS[presetKey];
    if (preset) {
      dispatch({ type: 'UPDATE_VISUALIZER', payload: preset });
      setShowPresets(false);
    }
  };

  return (
    <header className="app-header bg-shop-dark border-b border-shop-border text-shop-light shrink-0">
      {/* Top Banner */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-4">
        {/* Brand / Vehicle Title */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 bg-safety-amber text-shop-darkest flex items-center justify-center rounded border border-safety-amberDark shadow-machined shrink-0 font-black">
            <Gauge className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs font-bold text-safety-amber uppercase tracking-wider bg-shop-darkest px-2 py-0.5 rounded border border-shop-border">
                RESTOMOD STUDIO
              </span>
              <span className="font-mono text-xs text-shop-muted">
                VIN: <strong className="text-shop-highlight">{state.project.vin}</strong>
              </span>
            </div>
            <h1 className="text-lg md:text-xl font-bold uppercase tracking-wide text-shop-highlight truncate">
              {state.project.title}
            </h1>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {/* Preset Quick Loader */}
          <div className="relative">
            <MechanicalButton
              variant="secondary"
              size="sm"
              icon={<Sparkles className="w-4 h-4 text-safety-amber" />}
              onClick={() => setShowPresets(!showPresets)}
              title="Load Stance and Wheel Presets"
            >
              Presets
            </MechanicalButton>

            {showPresets && (
              <div className="absolute right-0 top-full mt-1.5 w-64 bg-shop-panel border border-shop-border shadow-tactile rounded z-50 p-2 space-y-1">
                <div className="px-2 py-1 text-[11px] font-mono font-bold uppercase tracking-wider text-shop-muted border-b border-shop-border">
                  Workshop Stance Presets
                </div>
                {Object.entries(WORKSHOP_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-shop-light hover:text-shop-highlight hover:bg-shop-well rounded flex items-center justify-between cursor-pointer min-h-[44px]"
                  >
                    <span>{preset.name}</span>
                    <span className="text-[10px] font-mono text-safety-amber uppercase">
                      {preset.silhouetteId.replace('_', ' ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Export JSON */}
          <MechanicalButton
            variant="outline"
            size="sm"
            icon={<Download className="w-4 h-4 text-blueprint-cyan" />}
            onClick={handleExport}
            title="Export project state as JSON file"
          >
            Export
          </MechanicalButton>

          {/* Import JSON */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportFile}
            accept=".json,application/json"
            className="hidden"
          />
          <MechanicalButton
            variant="outline"
            size="sm"
            icon={<Upload className="w-4 h-4 text-shop-muted" />}
            onClick={() => fileInputRef.current?.click()}
            title="Import project JSON file"
          >
            Import
          </MechanicalButton>

          {/* Print Dossier */}
          <MechanicalButton
            variant="primary"
            size="sm"
            icon={<Printer className="w-4 h-4" />}
            onClick={handlePrint}
            title="Open print dialog for agreed-value underwriting dossier"
          >
            Print Dossier
          </MechanicalButton>

          {/* Reset State */}
          <MechanicalButton
            variant="outline"
            size="sm"
            icon={<RotateCcw className="w-4 h-4 text-safety-red" />}
            onClick={() => {
              if (window.confirm('Reset all project car data to factory default 1969 Charger?')) {
                resetToDefault();
              }
            }}
            title="Reset project to default baseline"
          >
            Reset
          </MechanicalButton>
        </div>
      </div>
    </header>
  );
};
